/**
 * @fileoverview Instructor consent ledger page — the web view of the
 * append-only `consents` table behind GET /consent/ledger.
 *
 * Ported from the mobile ConsentLedgerScreen. Lists every consent request,
 * grant, decline, and revocation for the instructor's students (newest first)
 * and exports the full history as CSV via a browser blob download. The CSV
 * keeps spreadsheet formula-injection neutralization and deliberately excludes
 * `token_hash`.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, Mail, CheckCircle2, XCircle, ShieldCheck, Link2, HelpCircle, CloudOff, ShieldQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sidebar, MobileNav } from "@/components/Sidebar";

// Display config per ledger event type. Unknown event types (a newer backend
// than this app build) fall back to the raw event name so nothing is hidden.
const EVENT_DISPLAY = {
  requested: { label: "Consent requested", Icon: Mail, color: "var(--state-warning)" },
  resent: { label: "Request resent", Icon: Mail, color: "var(--state-warning)" },
  granted: { label: "Consent granted", Icon: CheckCircle2, color: "var(--state-success)" },
  granted_offline: { label: "Consent granted (offline)", Icon: CheckCircle2, color: "var(--state-success)" },
  declined: { label: "Consent declined", Icon: XCircle, color: "var(--state-danger)" },
  revoked: { label: "Consent revoked", Icon: XCircle, color: "var(--state-danger)" },
  confirmation_sent: { label: "COPPA confirmation sent", Icon: ShieldCheck, color: "var(--text-tertiary)" },
  revoke_token_issued: { label: "Revoke link issued", Icon: Link2, color: "var(--text-quaternary)" },
};

const AUDIENCE_LABELS = { parent: "By parent", student: "By student" };
const AGE_BAND_LABELS = { adult: "Adult", "13_17": "13–17", under_13: "Under 13" };
const REASON_LABELS = {
  parent_revoke: "Via revoke link",
  email_plus_cancel: "Via confirmation cancel link",
};

// Backend timestamps are Python isoformat with microseconds. Trim to 3-digit
// fractional seconds so the Date parser can't reject them.
function parseTimestamp(iso) {
  return new Date(String(iso).replace(/\.(\d{3})\d+/, ".$1"));
}

function formatDate(iso) {
  if (!iso) return "";
  const d = parseTimestamp(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatTime(iso) {
  if (!iso) return "";
  const d = parseTimestamp(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

// Optional ConsentEvent fields are absent (not null) on stored docs, so every
// accessor below must tolerate missing keys.
function eventDetails(item) {
  return [
    AUDIENCE_LABELS[item.audience],
    AGE_BAND_LABELS[item.age_band],
    item.consent_text_version ? `Text v${item.consent_text_version}` : null,
    REASON_LABELS[item.reason] || item.reason,
    item.cc_teen === true ? "Teen copied" : null,
    item.ip ? `IP ${item.ip}` : null,
  ].filter(Boolean).join(" · ");
}

function csvEscape(value) {
  if (value === undefined || value === null) return "";
  let s = String(value);
  // Neutralize spreadsheet formula injection: user_agent and student names
  // are not under our control, and this file is meant to be opened in Excel.
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const CSV_HEADER = [
  "timestamp", "event", "student_name", "student_id", "audience", "age_band",
  "consent_text_version", "ip", "user_agent", "reason", "cc_teen",
  "expires_at", "consent_id",
].join(",");

// Chronological (oldest first), matching the ledger's append order. The
// token_hash field is deliberately excluded — it has no audit value outside
// the backend and shouldn't leave the system.
function buildCsv(events, nameForStudent) {
  const rows = events.map((e) => [
    e.created_at, e.event, nameForStudent(e.student_id), e.student_id,
    e.audience, e.age_band, e.consent_text_version, e.ip, e.user_agent,
    e.reason, e.cc_teen, e.expires_at, e.consent_id,
  ].map(csvEscape).join(","));
  return [CSV_HEADER, ...rows].join("\n");
}

const ConsentLedgerPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const cancelledRef = useRef(false);

  const studentNameById = useMemo(() => {
    const map = new Map();
    for (const s of students) {
      map.set(s.student_id, `${s.first_name || ""} ${s.last_name || ""}`.trim());
    }
    return map;
  }, [students]);

  const fetchLedger = useCallback(async (signal) => {
    try {
      const res = await api.get("/consent/ledger", signal ? { signal } : {});
      if (cancelledRef.current) return;
      // The endpoint returns oldest-first; the feed reads newest-first.
      // UTC ISO-8601 strings compare correctly as plain strings.
      const sorted = [...(res.data?.events || [])].sort(
        (a, b) => (b.created_at || "").localeCompare(a.created_at || "")
      );
      setEvents(sorted);
      setLoadFailed(false);
    } catch (_e) {
      // A silently-empty ledger reads as "no consent history", a compliance
      // statement — surface the failure instead.
      if (!signal?.aborted && !cancelledRef.current) setLoadFailed(true);
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    const controller = new AbortController();
    fetchLedger(controller.signal);
    api
      // `status=all` (I2): the ledger is an append-only COPPA record and this
      // map names its rows — including the CSV export. Archiving a student must
      // never blank their name in a compliance artifact.
      .get("/students?status=all", { signal: controller.signal })
      .then((res) => setStudents(res.data || []))
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) setStudentsLoading(false);
      });
    return () => {
      cancelledRef.current = true;
      controller.abort();
    };
  }, [fetchLedger]);

  const onRetry = () => {
    setLoading(true);
    setLoadFailed(false);
    fetchLedger();
  };

  const handleExport = () => {
    // Export in append order (oldest first), like the raw ledger.
    const chronological = [...events].reverse();
    const csv = buildCsv(chronological, (id) => studentNameById.get(id) || "");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hans-consent-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Consent ledger exported");
  };

  return (
    <div className="min-h-screen bg-[var(--studio-base)]">
      <Sidebar active="settings" />
      <MobileNav active="settings" />

      <main className="p-4 md:p-8 page-with-nav" data-testid="consent-ledger">
        <Button
          variant="ghost"
          onClick={() => navigate("/settings")}
          className="text-[var(--text-tertiary)] active:text-[var(--text-primary)] mb-4 md:mb-6 -ml-2 touch-target"
          data-testid="consent-ledger-back"
        >
          <ArrowLeft className="w-5 h-5 mr-2" /> Settings
        </Button>

        <div className="flex items-center justify-between mb-6 md:mb-8">
          <div>
            <p className="text-[10px] md:text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-1">Compliance</p>
            <h1 className="text-2xl md:text-4xl font-light tracking-tight text-[var(--text-primary)]" style={{ fontFamily: "Outfit, sans-serif" }}>
              Consent Ledger
            </h1>
          </div>
          {events.length > 0 && (
            <Button
              onClick={handleExport}
              variant="outline"
              className="border-[var(--studio-divider-strong)] text-[var(--text-secondary)] rounded-full px-5 py-2 touch-target"
              data-testid="export-ledger-button"
            >
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          )}
        </div>

        <div className="max-w-2xl">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-16 rounded-xl skeleton" />
              ))}
            </div>
          ) : loadFailed ? (
            <div className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-2xl p-10 md:p-16 text-center" data-testid="ledger-load-failed">
              <CloudOff className="w-10 h-10 text-[var(--text-quaternary)] mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-[var(--text-tertiary)] text-sm mb-6">Couldn't load the consent ledger</p>
              <Button onClick={onRetry} className="bg-[var(--accent-yellow)] text-[var(--text-inverse)] rounded-full touch-target" data-testid="ledger-retry-button">
                Retry
              </Button>
            </div>
          ) : events.length === 0 ? (
            <div className="bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-2xl p-10 md:p-16 text-center" data-testid="ledger-empty">
              <ShieldQuestion className="w-10 h-10 text-[var(--text-quaternary)] mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-[var(--text-tertiary)] text-sm">No consent activity yet</p>
              <p className="text-[var(--text-quaternary)] text-xs mt-2">
                Consent requests, grants, and revocations for your students will appear here.
              </p>
            </div>
          ) : (
            <>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-quaternary)] mb-3">
                {events.length} event{events.length === 1 ? "" : "s"}
              </p>
              <div className="space-y-2.5">
                {events.map((item, index) => {
                  const display = EVENT_DISPLAY[item.event] || { label: item.event, Icon: HelpCircle, color: "var(--text-tertiary)" };
                  const { Icon } = display;
                  const studentName = studentNameById.get(item.student_id) || (studentsLoading ? "…" : "Former student");
                  const details = eventDetails(item);
                  return (
                    <motion.div
                      key={item.consent_id || index}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index * 0.02, 0.2) }}
                      className="flex items-center gap-3 p-3.5 bg-[var(--studio-raised)] border border-[var(--studio-divider)] rounded-xl"
                      data-testid={`ledger-event-${index}`}
                    >
                      <div className="w-9 h-9 bg-[var(--studio-interactive)] rounded-full flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4" style={{ color: display.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium" style={{ color: display.color }}>{display.label}</p>
                        <p className="text-[var(--text-secondary)] text-xs mt-0.5">{studentName}</p>
                        {details && <p className="text-[var(--text-quaternary)] text-[11px] mt-1">{details}</p>}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[var(--text-tertiary)] text-xs">{formatDate(item.created_at)}</p>
                        <p className="text-[var(--text-quaternary)] text-[11px] mt-0.5">{formatTime(item.created_at)}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ConsentLedgerPage;
